import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { GitCompare, TrendingUp, TrendingDown, ArrowRight, RefreshCw, Skull, Shield, Bug, AlertTriangle, Cable } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import ShareViewButton from '../components/ShareViewButton'

interface Diff {
  window_days: number
  current: { start: string; end: string }
  prior:   { start: string; end: string }
  cves: { current: any; prior: any; delta_total_pct: number; delta_critical_pct: number }
  kev:  { current: number; prior: number; delta_pct: number; new_this_week: any[] }
  advisory_by_vendor: { vendor: string; current: number; prior: number; delta_pct: number }[]
  ransomware: {
    victims_current: number; victims_prior: number; delta_pct: number
    gangs:          { gang: string; current: number; prior: number; delta: number; status: string }[]
    new_gangs:      { gang: string; current: number }[]
    retired_gangs:  { gang: string; prior: number }[]
  }
  hot_actors: { name: string; sponsor: string; heat: number; reasons: string[] }[]
  cables: { current_faults: number; prior_faults: number; faulted_cables: string[] }
}

function DeltaBadge({ pct, inverse }: { pct: number; inverse?: boolean }) {
  // inverse=true means "going up is bad" (e.g. ransomware victims) — colour flipped
  const up = pct > 0
  const down = pct < 0
  const good = (down && !inverse) || (up && inverse)
  const bad  = (up && !inverse) || (down && inverse)
  const Icon = up ? TrendingUp : down ? TrendingDown : ArrowRight
  return (
    <span className={clsx(
      'inline-flex items-center gap-0.5 text-[11px] font-mono font-semibold',
      good ? 'text-emerald-light' : bad ? 'text-rose-light' : 'text-text-muted',
    )}>
      <Icon size={10} />
      {pct > 0 ? '+' : ''}{pct}%
    </span>
  )
}

export default function WeeklyDiffPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlDays = parseInt(searchParams.get('days') || '7', 10)
  const [data, setData] = useState<Diff | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(Number.isFinite(urlDays) ? urlDays : 7)

  useEffect(() => {
    const next = new URLSearchParams()
    if (days !== 7) next.set('days', String(days))
    setSearchParams(next, { replace: true })
  }, [days, setSearchParams])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/cyber/diff', { params: { days } })
      setData(r.data)
    } finally { setLoading(false) }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <GitCompare size={24} className="text-blue" /> What Changed
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Cyber landscape · last {days}d vs prior {days}d
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[10px]">
            {[7, 14, 30].map(d => (
              <button key={d} type="button" onClick={() => setDays(d)}
                className={clsx('px-2 py-1', days === d ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover')}>
                {d}d
              </button>
            ))}
          </div>
          <button type="button" onClick={fetchData} className="btn-secondary text-xs">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <ShareViewButton />
        </div>
      </div>

      {!data && loading && <div className="card p-6 text-center text-text-muted text-sm">Computing diff…</div>}

      {data && (
        <>
          {/* Top KPI bar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard icon={Bug} tone="amber" label="CVEs published"
              cur={data.cves.current.total} prior={data.cves.prior.total} delta={data.cves.delta_total_pct} inverse />
            <KpiCard icon={Shield} tone="rose" label="CVSS ≥ 9 critical"
              cur={data.cves.current.critical} prior={data.cves.prior.critical} delta={data.cves.delta_critical_pct} inverse />
            <KpiCard icon={AlertTriangle} tone="rose" label="KEV additions"
              cur={data.kev.current} prior={data.kev.prior} delta={data.kev.delta_pct} inverse />
            <KpiCard icon={Skull} tone="purple" label="Ransomware victims"
              cur={data.ransomware.victims_current} prior={data.ransomware.victims_prior} delta={data.ransomware.delta_pct} inverse />
            <KpiCard icon={Cable} tone="cyan" label="Cable fault reports"
              cur={data.cables.current_faults} prior={data.cables.prior_faults} delta={0} inverse />
          </div>

          {/* New KEV entries this window */}
          {data.kev.new_this_week.length > 0 && (
            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-rose" /> New CISA KEV this {days}d
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.kev.new_this_week.map((k, i) => (
                  <Link key={i} to={`/app/cve/${encodeURIComponent(k.cve_id)}`}
                    className="flex items-center justify-between p-2 rounded border border-border hover:bg-surface-hover text-xs">
                    <div>
                      <div className="font-mono text-blue-light">{k.cve_id}</div>
                      <div className="text-[10px] text-text-muted">{k.vendor} {k.product}</div>
                    </div>
                    {k.ransomware && <span className="badge-red text-[10px]">Ransomware</span>}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Ransomware gang diff */}
          {(data.ransomware.new_gangs.length > 0 || data.ransomware.retired_gangs.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card p-4">
                <h3 className="text-xs font-semibold text-emerald-light mb-2">
                  🆕 Emerged gangs ({data.ransomware.new_gangs.length})
                </h3>
                <div className="space-y-1 max-h-[220px] overflow-y-auto">
                  {data.ransomware.new_gangs.slice(0, 20).map(g => (
                    <div key={g.gang} className="flex justify-between text-xs">
                      <span className="font-mono text-text-secondary">{g.gang}</span>
                      <span className="text-emerald font-semibold">+{g.current}</span>
                    </div>
                  ))}
                  {data.ransomware.new_gangs.length === 0 && <p className="text-[11px] text-text-muted italic">None</p>}
                </div>
              </div>
              <div className="card p-4">
                <h3 className="text-xs font-semibold text-text-muted mb-2">
                  ⏸ Gone quiet ({data.ransomware.retired_gangs.length})
                </h3>
                <div className="space-y-1 max-h-[220px] overflow-y-auto">
                  {data.ransomware.retired_gangs.slice(0, 20).map(g => (
                    <div key={g.gang} className="flex justify-between text-xs">
                      <span className="font-mono text-text-secondary">{g.gang}</span>
                      <span className="text-rose font-semibold">−{g.prior}</span>
                    </div>
                  ))}
                  {data.ransomware.retired_gangs.length === 0 && <p className="text-[11px] text-text-muted italic">None</p>}
                </div>
              </div>
            </div>
          )}

          {/* Vendor advisories */}
          {data.advisory_by_vendor.length > 0 && (
            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary mb-3">Vendor advisories — vs prior {days}d</h2>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                    <th className="text-left py-2 px-2">Vendor</th>
                    <th className="text-right py-2 px-2">This {days}d</th>
                    <th className="text-right py-2 px-2">Prior</th>
                    <th className="text-right py-2 px-2">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.advisory_by_vendor.map(v => (
                    <tr key={v.vendor} className="border-b border-border/50">
                      <td className="py-1.5 px-2 text-text-primary font-medium">{v.vendor}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{v.current}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-text-muted">{v.prior}</td>
                      <td className="py-1.5 px-2 text-right"><DeltaBadge pct={v.delta_pct} inverse /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Top movers — ransomware gangs by absolute delta */}
          {data.ransomware.gangs.length > 0 && (
            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary mb-3">Ransomware gangs — top movers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.ransomware.gangs.slice(0, 14).map(g => (
                  <div key={g.gang} className="flex items-center justify-between p-2 rounded border border-border text-xs">
                    <span className="font-mono text-text-primary">{g.gang}</span>
                    <div className="flex items-center gap-3 text-[11px]">
                      <span className="text-text-muted">{g.prior} → <b className="text-text-primary">{g.current}</b></span>
                      <span className={clsx('font-semibold', g.delta > 0 ? 'text-rose-light' : g.delta < 0 ? 'text-emerald-light' : 'text-text-muted')}>
                        {g.delta > 0 ? '+' : ''}{g.delta}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hot actors right now */}
          {data.hot_actors.length > 0 && (
            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary mb-3">Hot threat actors right now</h2>
              <div className="space-y-1">
                {data.hot_actors.map(a => (
                  <div key={a.name} className="flex items-center gap-3 p-2 rounded hover:bg-surface-hover text-xs">
                    <span className="font-mono text-text-primary w-32">{a.name}</span>
                    <span className="text-text-muted w-20">{a.sponsor}</span>
                    <span className="font-mono text-text-primary w-12 text-right">{a.heat}</span>
                    <span className="text-[11px] text-text-secondary flex-1">{a.reasons[0]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function KpiCard({ icon: Icon, tone, label, cur, prior, delta, inverse }: {
  icon: React.ElementType
  tone: 'rose' | 'amber' | 'purple' | 'cyan' | 'emerald'
  label: string
  cur: number
  prior: number
  delta: number
  inverse?: boolean
}) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber' : tone === 'purple' ? 'text-purple-400' : tone === 'cyan' ? 'text-cyan' : 'text-emerald'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1">
        <Icon size={10} className={t} /> {label}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-bold text-text-primary">{cur.toLocaleString()}</span>
        <DeltaBadge pct={delta} inverse={inverse} />
      </div>
      <div className="text-[9px] text-text-muted mt-0.5">prior: {prior.toLocaleString()}</div>
    </div>
  )
}
